<script lang="ts">
export default {
  name: 'KkConnectSite'
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useLocationConnectStore } from '../../../location/stores'
import { KkIcon, KkSiteIcon } from '../../../../components/base'
import type { DeleteConnectApiParams } from '../../../../domains/location/stores'
import { castCamelToSnake } from '../../../../modules/string_converter'

/** Interface */
interface Site {
  name: string
  siteType: string
  connected?: boolean
  storeId?: string
  canConnectStoreIdentifier: boolean
}

interface Props {
  site: Site
}

/** Props */
const props = defineProps<Props>()

/** Variable */
const route = useRoute()
const router = useRouter()
const store = useStore()
const { t } = useI18n()
interface Emits {
  (e: 'open-upgrade-modal', siteType: string): void
}
const emit = defineEmits<Emits>()

/** Pinia Store */
const locationConnectStore = useLocationConnectStore()
const { connects, isWriting } = storeToRefs(locationConnectStore)
const { readConnects, deleteConnect } = locationConnectStore

/** Vuex Store: state */
const storeGmbConnected = computed<boolean>(() => store.state.companiesStoreGmb.connected)

/** Vuex Store: getters */
const storeGmbName = computed<boolean>(() => store.getters['companiesStoreGmb/storeName'])
const showMoreThanEditorAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showMoreThanEditorAuthority'])
const isAdmin = computed<boolean>(() => store.getters['companiesAccessibleApi/isAdmin'])
const isDistributer = computed<boolean>(() => store.getters['companiesAccessibleApi/isDistributer'])
const showOnlyOwnerAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const providedFeaturesLinkUrl = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesLinkUrl'])
const providedFeaturesExternalAccount = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesExternalAccount'])
const isDocomoPlan = computed<boolean>(() => store.getters['companiesCompany/isDocomoPlan'])
const isRettyPlan = computed<boolean>(() => store.getters['companiesCompany/isRettyPlan'])
const useServiceName = computed<string>(() => store.getters['companiesCompany/useServiceName'])

/** Vuex Store: action */
const readStoreOcShufoo = () => store.dispatch('companiesStoreOcShufoo/readStoreOcShufoo')
const deleteStoreOcShufoo = () => store.dispatch('companiesStoreOcShufoo/deleteStoreOcShufoo')
const readStoreOcMapionBiz = () => store.dispatch('companiesStoreOcMapionBiz/readStoreOcMapionBiz')
const deleteStoreOcMapionBiz = () => store.dispatch('companiesStoreOcMapionBiz/deleteStoreOcMapionBiz')
const deleteStoreGmb = () => store.dispatch('companiesStoreGmb/deleteStoreGmb', {company_uuid: companyUuid.value, store_uuid: storeUuid.value})

/** Computed */
const companyUuid = computed<string>(() => route.params.company_uuid as string)
const storeUuid = computed<string>(() => route.params.store_uuid as string)
const connect = computed(() => {
  return connects.value.items.find(connect => connect.siteType === props.site.siteType)
})
const password = computed<string>(() => {
  if (connect.value?.account?.passwordLength) {
    return [...Array(connect.value.account.passwordLength)].map(() => '●').join('')
  } else {
    return ''
  }
})
const possibleConnectReview = computed<boolean>(() => {
  return connects.value.meta.reviewSiteTypes.includes(props.site.siteType)
})
const possibleConnectAccount = computed<boolean>(() => {
  return connects.value.meta.accountSiteTypes.includes(props.site.siteType)
})
const possibleConnectAccountStoreIdentifier = computed<boolean>(() => {
  return connects.value.meta.accountStoreIdentifierSiteTypes.includes(props.site.siteType)
})
const linkToFormName = computed<string>(() => {
  if (props.site.siteType === 'google') {
    return 'LocationStoreConnectGmb'
  } else if (props.site.siteType === 'shufoo') {
    return 'LocationStoreConnectShufoo'
  } else if (props.site.siteType === 'mapion_biz') {
    return 'LocationStoreConnectMapionbiz'
  } else {
    return 'LocationStoreConnectAccount'
  }
})
const isStoreGmbConnected = computed<boolean>(() => {
  return props.site.siteType === 'google' && storeGmbConnected.value
})
const canEdit = computed<boolean>(() => {
  if(!showMoreThanEditorAuthority.value) return false
  if(providedFeaturesLinkUrl.value && providedFeaturesExternalAccount.value) return true
  if(props.site.siteType !== 'google') return true

  return showOnlyOwnerAuthority.value && (isAdmin.value || isDistributer.value)
})
const connectedReview = computed<boolean>(() => {
  return Boolean(connect.value?.review.url || isStoreGmbConnected.value)
})
const connectedAccount = computed<boolean>(() => {
  if (isStoreGmbConnected.value) {
    return true
  }

  // Rettyプランの場合のRettyはAPIで連携しているので連携済みとする
  if (props.site.siteType === 'retty' && isRettyPlan.value) {
    return true
  } else if (props.site.canConnectStoreIdentifier && props.site.siteType !== 'gnavi') { // 楽天ぐるなびは店舗IDがなくてもRPAが実行できるのでアイパスがあれば連携済みとする
    return Boolean(connect.value?.account?.storeIdentifier)
  }  else {
    return Boolean(connect.value?.account?.identifier && connect.value?.account?.passwordLength)
  }
})
const storeGmbColspan = computed<number>(() => {
  const colspan = showMoreThanEditorAuthority.value ? 4 : 3
  return canUnconnectedGmb.value ? colspan - 1 : colspan
})
const canUnconnectedGmb = computed<boolean>(() => {
  return isAdmin.value || isDistributer.value
})

const canHandleConnection = computed<boolean>(() => {
  return props.site.siteType !== 'docomo'
})

const storeIdLabel = computed<string>(() => {
  return t(`domains.locationStore.components.KkConnectSite.${props.site.siteType === 'taishu_tenpyo' ? 'taishuTenpyoStoreName' : 'storeId'}`)
})

/** Methods */
const unconnected = async (connectType: 'accounts' | 'reviews') => {
  const confirmResult = confirm(t(`domains.locationStore.components.KkConnectSite.confirmToUnconnected.${ connectType }`))
  if (!confirmResult) return

  const params: DeleteConnectApiParams = {
    companyUuid: companyUuid.value,
    storeUuid: storeUuid.value,
    connectType,
    params: {
      siteType: props.site.siteType,
    }
  }
  const result = await deleteConnect(params)
  if (result === 'success') {
    readConnects({ companyUuid: companyUuid.value, storeUuid: storeUuid.value })
  }
}
const handleUnconnected = async (connectType: 'shufoo' | 'mapion_biz') => {
  const confirmResult = confirm(t('domains.locationStore.components.KkConnectSite.confirmToUnconnected.others'))
  if (!confirmResult) return

  if (connectType === 'shufoo') {
    const result = await deleteStoreOcShufoo()
    if (result) readStoreOcShufoo()
  }
  if (connectType === 'mapion_biz') {
    const result = await deleteStoreOcMapionBiz()
    if (result) readStoreOcMapionBiz()
  }
}

const clickSetting = () => {
  if(isDocomoPlan.value) {
    emit('open-upgrade-modal', props.site.siteType)
  } else {
    router.push({
      name: linkToFormName.value,
      params: {
        company_uuid: companyUuid.value,
        store_uuid: storeUuid.value,
        site_name: castCamelToSnake(props.site.siteType),
      }
    })
  }

}

const unconnectedGmb = async () => {
  const confirmResult = confirm(t('domains.locationStore.components.KkConnectSite.confirmToUnconnected.gmb'))
  if (!confirmResult) return

  await deleteStoreGmb()
}
</script>

<template>
  <!-- Docomo, Shufoo!, Mapion Biz -->
  <tr
    v-if="site.siteType === 'docomo' || site.siteType === 'shufoo' || site.siteType === 'mapion_biz'"
    class="connectSite"
    :class="{ 'is-unconnected': !site.connected }"
  >
    <td class="connectSite__column connectSite__column--icon">
      <div class="connectSite__icon">
        <KkSiteIcon :name="props.site.siteType" />
      </div>
    </td>
    <td class="connectSite__column connectSite__column--summary">
      <p class="connectSite__header">{{ props.site.name }}</p>
      <ul class="connectSite__status">
        <li :class="{ 'is-active': site.connected }">
          {{ $t(`domains.locationStore.components.KkConnectSite.${ site.connected ? 'connected' : 'unconnected' }`) }}
        </li>
      </ul>
    </td>
    <td class="connectSite__column connectSite__column--review">
      <p class="connectSite__header">
        {{ t('domains.locationStore.components.KkConnectSite.storeId') }}
      </p>
      <p class="connectSite__reviewUrl">{{ site.storeId ?? '-' }}</p>
    </td>
    <td class="connectSite__column connectSite__column--account" />
    <td class="connectSite__column connectSite__column--storeId" />
    <td class="connectSite__column connectSite__column--action">
      <template v-if="canHandleConnection">
        <button
          v-if="site.connected"
          type="button"
          class="connectSite__action"
          @click="handleUnconnected(site.siteType as 'shufoo' | 'mapion_biz')"
        >
          <span class="connectSite__action__icon">
            <KkIcon name="delete" color="#3A3B3F" />
          </span>
          {{ t('domains.locationStore.components.KkConnectSite.disconnectAction') }}
        </button>
        <router-link
          v-else
          :to="{
            name: linkToFormName,
            params: {
              company_uuid: companyUuid,
              store_uuid: storeUuid,
            }
          }"
          class="connectSite__action"
        >
          <span class="connectSite__action__icon">
            <KkIcon name="detail" color="#3A3B3F" />
          </span>
          {{ t('domains.locationStore.components.KkConnectSite.setting') }}
        </router-link>
      </template>
    </td>
  </tr>
  <!-- GMB, その他サイト -->
  <tr
    v-else
    class="connectSite"
    :class="{ 'is-unconnected': !connect && !isStoreGmbConnected }"
  >
    <td class="connectSite__column connectSite__column--icon">
      <div class="connectSite__icon">
        <KkSiteIcon :name="props.site.siteType" />
      </div>
    </td>
    <td class="connectSite__column connectSite__column--summary">
      <p class="connectSite__header">{{ props.site.name }}</p>
      <ul class="connectSite__status">
        <li :class="{ 'is-active': connectedReview }">
          {{ t('domains.locationStore.components.KkConnectSite.reviews') }}
        </li>
        <li :class="{ 'is-active': connectedAccount }">
          {{ t('domains.locationStore.components.KkConnectSite.storeInformation') }}
        </li>
      </ul>
    </td>
    <template v-if="isStoreGmbConnected">
      <td
        class="connectSite__column connectSite__column--connected"
        :colspan="storeGmbColspan"
      >
        {{ t('domains.locationStore.components.KkConnectSite.connectedWith', { name: storeGmbName }) }}
      </td>
      <td v-if="canUnconnectedGmb" class="connectSite__column connectSite__column--action">
        <div class="connectSite__action connectSite__unconnected">
          <span class="connectSite__action__icon">
            <KkIcon name="delete" color="#3A3B3F" />
          </span>
          {{ t('domains.locationStore.components.KkConnectSite.disconnectAction') }}
          <div class="connectSite__buttons">
            <button
              type="button"
              class="connectSite__button"
              @click="unconnectedGmb()"
            >
              {{ t('domains.locationStore.components.KkConnectSite.disconnectGoogleApi') }}
            </button>
          </div>
        </div>
      </td>
    </template>
    <template v-else>
      <td class="connectSite__column connectSite__column--review">
        <template v-if="possibleConnectReview">
          <p class="connectSite__header">
            {{ t('domains.locationStore.components.KkConnectSite.url') }}
          </p>
          <p class="connectSite__reviewUrl">{{ connect?.review.url ?? '-' }}</p>
        </template>
      </td>
      <td class="connectSite__column connectSite__column--account">
        <template v-if="possibleConnectAccount && useServiceName !== 'retty'">
          <p class="connectSite__header">
            {{ t('domains.locationStore.components.KkConnectSite.account') }}
          </p>
          <p class="connectSite__accountId">{{ connect?.account?.identifier ?? '-' }}</p>
          <p v-if="password" class="connectSite__accountPassword">
            {{ password }}
          </p>
        </template>
      </td>
      <td class="connectSite__column connectSite__column--storeId">
        <template v-if="possibleConnectAccountStoreIdentifier && useServiceName !== 'retty'">
          <p class="connectSite__header">
            {{ storeIdLabel }}
          </p>
          <p class="connectSite__storeId">{{ connect?.account?.storeIdentifier ?? '-' }}</p>
        </template>
      </td>
      <td v-if="canEdit" class="connectSite__column connectSite__column--action">
        <template v-if="connect">
          <router-link
            :to="{
              name: linkToFormName,
              params: {
                company_uuid: companyUuid,
                store_uuid: storeUuid,
                site_name: castCamelToSnake(props.site.siteType),
              }
            }"
            class="connectSite__action"
          >
            <span class="connectSite__action__icon">
              <KkIcon name="edit" color="#3A3B3F" />
            </span>
            {{ t('domains.locationStore.components.KkConnectSite.edit') }}
          </router-link>
          <div class="connectSite__action connectSite__unconnected">
            <span class="connectSite__action__icon">
              <KkIcon name="delete" color="#3A3B3F" />
            </span>
            {{ t('domains.locationStore.components.KkConnectSite.disconnectAction') }}
            <div v-if="!isWriting" class="connectSite__buttons">
              <button
                v-if="connect?.review.url"
                type="button"
                class="connectSite__button"
                :disabled="isWriting"
                @click="unconnected('reviews')"
              >
                {{ t('domains.locationStore.components.KkConnectSite.disconnectReviews') }}
              </button>
              <button
                v-if="connect?.account?.identifier"
                type="button"
                class="connectSite__button"
                :disabled="isWriting"
                @click="unconnected('accounts')"
              >
                {{ t('domains.locationStore.components.KkConnectSite.disconnectAccounts') }}
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <button
            class="connectSite__action"
            @click="clickSetting"
          >
            <span class="connectSite__action__icon">
              <KkIcon name="detail" color="#3A3B3F" />
            </span>
            {{ t('domains.locationStore.components.KkConnectSite.setting') }}
          </button>
        </template>
      </td>
    </template>
  </tr>
</template>

<style lang="scss" scoped>
.connectSite {
  $root: #{&};
  padding: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  &:not(:first-child) {
    margin-top: 8px;
  }
  &.is-unconnected {
    #{$root}__column {
      background-color: #F8F9FA;
    }
    #{$root}__icon {
      filter: grayscale(1) opacity(.4);
    }
  }
  &__column {
    padding-top: 24px;
    padding-bottom: 24px;
    border: 1px solid #E4E7EB;
    vertical-align: top;
    word-break: break-all;
    &:first-child {
      padding: 24px;
      border-right: 0;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
    &:not(:first-child) {
      padding-right: 24px;
      border-left: 0;
    }
    &:not(:first-child):not(:last-child) {
      border-right: 0;
    }
    &--icon {
      width: 112px;
    }
    &--connected {
      vertical-align: middle;
      color: #E9446A;
    }
    &--review {
      width: 28%;
    }
    &--account {
      width: 20%;
    }
    &--storeId {
      width: 12%;
    }
    &--action {
      width: 180px;
      vertical-align: middle;
    }
  }
  &__header {
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.28;
    white-space: nowrap;
  }
  &__icon {
    width: 64px;
    height: 64px;
    padding: 8px;
    border: 1px solid #E4E7EB;
    border-radius: 4px;
  }
  &__status {
    display: flex;
    gap: 8px;
    > li {
      height: 20px;
      padding: 4px 6px;
      border-radius: 12px;
      background-color: #D2D5DB;
      color: #FFF;
      font-weight: 700;
      font-size: 10px;
      line-height: 1.2;
      white-space: nowrap;
      &.is-active {
        background-color: #4C959A;
      }
    }
  }
  &__action {
    display: flex;
    align-items: center;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
    &:not(:first-child) {
      margin-top: 8px;
    }
    &__icon {
      width: 16px;
      margin-right: 10px;
      opacity: .7;
    }
  }
  &__buttons {
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 1;
    padding: 8px 0;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .05);
    background: #FFF;
  }
  &__button {
    display: block;
    width: 100%;
    padding: 12px 24px;
    font-size: 14px;
    line-height: 1.72;
    white-space: nowrap;
    transition: .2s;
    &:hover {
      background-color: #eee;
    }
  }
  &__unconnected {
    position: relative;
    cursor: pointer;
    &:hover {
      #{$root}__buttons {
        display: block
      }
    }
  }
  @media screen and (max-width: 991px) {
    display: block;
    padding: 16px 0;
    border: 1px solid #E4E7EB;
    border-radius: 4px;
    &.is-unconnected {
      background: #F8F9FA;
      #{$root}__column {
        background-color: transparent;
      }
    }
    &__column {
      display: block;
      padding: 0 16px;
      border: 0;
      &:first-child {
        width: auto;
        padding: 0;
      }
      &:not(:first-child) {
        margin-top: 24px;
      }
      &--review,
      &--account,
      &--storeId {
        width: auto;
      }
      &--action {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        width: auto;
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #E4E7EB;
      }
    }
    &__icon {
      margin: 0 auto;
    }
    &__action {
      &:not(:first-child) {
        margin-top: 0;
      }
      &__icon {
        margin-right: 6px;
      }
    }
  }
}
</style>
